import React, { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import useHttpHook from "../../../Hooks/HttpHook";
import Input from "../../UiElement/FormElement/Input";
import MainComponentWrapper from "../../Common/MainComponentWrapper";
import {
  AssignGuardApi,
  AssignGuardToEvent,
  DeleteGuard,
  GetAllOrganizerEventsApi,
  GetOrganizerGuardsList,
} from "../../../utils/ApiConstant";
import { GuardLanguage } from "../../../utils/Languages";
import { createObjectForPaginatedResult } from "../../../utils/Helper";
import ModalWrapper from "../../Common/Portals/ModalWrapper";
import EditGuard from "./EditGuard";
import { toast } from "react-toastify";
import MainPagination from "../../Common/MainPagination";
import ChangePassword from "./ChangePassword";
import { showErrorToast } from "../../../utils/notify";

const toId = (value) => (value == null ? "" : String(value));

const sameIds = (a = [], b = []) => {
  const left = [...a].map(toId).filter(Boolean).sort();
  const right = [...b].map(toId).filter(Boolean).sort();
  if (left.length !== right.length) return false;
  return left.every((id, index) => id === right[index]);
};

const AssignGuard = ({ isOpen, setIsOpen }) => {
  const [loading, sendRequest, error] = useHttpHook();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();
  const [events, setEvents] = useState([]);
  const [guards, setGuards] = useState(null);
  const [guardState, setGuardState] = useState([]);
  const [savedGuardIds, setSavedGuardIds] = useState([]);
  const [event, setEvent] = useState("");
  const [dobError, setDobError] = useState("");
  const [selectedGuard, setSelectedGuard] = useState("");
  const [isDeleteModal, setIsDeleteModal] = useState(false);
  const [isEditModal, setIsEditModal] = useState(false);
  const [search, setSearch] = useState("");
  const [changePassword, setChangePassword] = useState(null);
  const [saving, setSaving] = useState(false);
  const skipSearchFetchRef = useRef(true);

  const guardItems = guards?.data?.itemsList || [];
  const hasUnsavedChanges = Boolean(event) && !sameIds(guardState, savedGuardIds);

  const getOrganizerEvents = useCallback(async () => {
    try {
      const res = await sendRequest(
        `${GetAllOrganizerEventsApi}?isLatest=${true}`,
        { method: "GET" }
      );

      if (res.status === 1) {
        setEvents(Array.isArray(res.data) ? res.data : []);
      }
    } catch (fetchError) {
      console.log({ fetchError });
    }
  }, [sendRequest]);

  const getOrganizerGuardsList = useCallback(
    async (page = 1) => {
      try {
        const res = await sendRequest(
          `${GetOrganizerGuardsList}?page=${page}&search=${encodeURIComponent(
            search
          )}`,
          { method: "GET" }
        );

        if (page === 1) {
          setGuards(res);
        } else {
          setGuards((prevState) =>
            createObjectForPaginatedResult(res, prevState, "paginate")
          );
        }
      } catch (fetchError) {
        console.log({ fetchError });
      }
    },
    [search, sendRequest]
  );

  const syncSelectionForEvent = useCallback((eventId, eventList = []) => {
    if (!eventId) {
      setGuardState([]);
      setSavedGuardIds([]);
      return;
    }

    const matched = eventList.find((item) => toId(item._id) === toId(eventId));
    const ids = (matched?.guard || []).map(toId);
    setGuardState(ids);
    setSavedGuardIds(ids);
  }, []);

  useEffect(() => {
    syncSelectionForEvent(event, events);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, syncSelectionForEvent]);

  useEffect(() => {
    getOrganizerEvents();
  }, [getOrganizerEvents]);

  useEffect(() => {
    const delay = skipSearchFetchRef.current ? 0 : 400;
    if (skipSearchFetchRef.current) {
      skipSearchFetchRef.current = false;
    }

    const debounceTimeout = setTimeout(() => {
      getOrganizerGuardsList();
    }, delay);

    return () => clearTimeout(debounceTimeout);
  }, [search, getOrganizerGuardsList]);

  useEffect(() => {
    if (error && toast.isActive(error) === false) {
      showErrorToast(error);
      if (error === "You are unAuthrised") {
        window.location.href = `${process.env.REACT_APP_MERCH_URL}/login`;
      }
    }
  }, [error]);

  const gender = [
    { title: "male" },
    { title: "female" },
    { title: "other" },
  ];

  const toggleGuardSelection = (guardId) => {
    if (!event) {
      toast.info("Select an event first");
      return;
    }

    const id = toId(guardId);
    if (!id) return;

    setGuardState((prev) => {
      const normalized = (prev || []).map(toId);
      if (normalized.includes(id)) {
        return normalized.filter((item) => item !== id);
      }
      return [...normalized, id];
    });
  };

  const addGuard = async (data) => {
    try {
      const dob = new Date(data.dob);
      if (dob > Date.now()) {
        setDobError("Date of Birth should be less than current date");
        return;
      }

      setDobError("");

      const res = await sendRequest(AssignGuardToEvent, {
        method: "POST",
        data: {
          guard: {
            name: data.name,
            email: data.email,
            phone: data.phone,
            password: data.password,
            dob: data.dob,
            gender: data.gender,
            userName: data.userName,
          },
        },
      });

      if (res.status === 1) {
        setIsOpen(false);
        toast.success(res.message || "Guard created");
        getOrganizerGuardsList();
        reset({
          name: "",
          email: "",
          phone: "",
          password: "",
          dob: "",
          gender: "",
          userName: "",
        });
      }
    } catch (submitError) {
      console.log({ submitError });
    }
  };

  const handleDeleteGuard = async () => {
    try {
      const res = await sendRequest(`${DeleteGuard}/${selectedGuard}`, {
        method: "delete",
      });

      if (res.status === 1) {
        toast.success(res.message || "Guard deleted");
        getOrganizerGuardsList();
        const refreshedEvents = await sendRequest(
          `${GetAllOrganizerEventsApi}?isLatest=${true}`,
          { method: "GET" }
        );
        if (refreshedEvents.status === 1) {
          const nextEvents = Array.isArray(refreshedEvents.data)
            ? refreshedEvents.data
            : [];
          setEvents(nextEvents);
          syncSelectionForEvent(event, nextEvents);
        }
        setIsDeleteModal(false);
        setSelectedGuard("");
      }
    } catch (deleteError) {
      console.log(deleteError);
    }
  };

  const assignGuard = async () => {
    if (!event) {
      toast.error("Please select an event first");
      return;
    }

    if (!hasUnsavedChanges) {
      toast.info("No changes to save");
      return;
    }

    try {
      setSaving(true);
      const nextIds = (guardState || []).map(toId);
      const res = await sendRequest(AssignGuardApi, {
        method: "POST",
        data: {
          guard: nextIds,
          event: toId(event),
        },
      });

      if (res.status === 1) {
        toast.success(res.message || "Guards saved");
        setSavedGuardIds(nextIds);
        const refreshed = await sendRequest(
          `${GetAllOrganizerEventsApi}?isLatest=${true}`,
          { method: "GET" }
        );
        if (refreshed.status === 1) {
          setEvents(Array.isArray(refreshed.data) ? refreshed.data : []);
        }
        await getOrganizerGuardsList();
      }
    } catch (assignError) {
      console.log({ assignError });
    } finally {
      setSaving(false);
    }
  };

  return (
    <MainComponentWrapper errorInfo={error} Loading={loading && !saving}>
      <div className="event-guard-body">
        <div className="event-guard-toolbar-row">
          <select
            id="assignGuardEvent"
            className="event-guard-filter-select"
            value={event}
            onChange={(e) => setEvent(toId(e.target.value))}
            aria-label="Select event"
          >
            <option value="">Select event</option>
            {events.map((item) => (
              <option key={toId(item._id)} value={toId(item._id)}>
                {item.title}
              </option>
            ))}
          </select>

          <input
            id="guardNameFilter"
            type="text"
            className="event-guard-filter-input"
            placeholder="Search guards"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search guards"
          />

          <button
            type="button"
            className="event-guard-header-btn"
            onClick={assignGuard}
            disabled={!event || !hasUnsavedChanges || saving || loading}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>

        <div className="event-guard-table-wrap">
          {guards !== null && (
            <MainPagination
              getEventsMethod={getOrganizerGuardsList}
              MainData={guards}
              PaginationType="paginate"
            >
              <table className="event-guard-table" aria-label="Guards list">
                <thead>
                  <tr>
                    <th scope="col" className="is-select">
                      <span className="visually-hidden">Assign</span>
                    </th>
                    <th scope="col">Name</th>
                    <th scope="col">Email</th>
                    <th scope="col">Phone</th>
                    <th scope="col" className="is-action">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {guardItems.length > 0 ? (
                    guardItems.map((guard, index) => {
                      const guardId = toId(guard._id);
                      const isChecked = guardState.map(toId).includes(guardId);

                      return (
                        <tr key={guardId || index}>
                          <td className="is-select" data-label="Assign">
                            <label
                              className="event-guard-check"
                              htmlFor={`guard-${guardId}`}
                            >
                              <input
                                type="checkbox"
                                name="guard"
                                id={`guard-${guardId}`}
                                disabled={!event}
                                onChange={() => toggleGuardSelection(guardId)}
                                checked={isChecked}
                                aria-label={`Assign ${guard.name}`}
                              />
                            </label>
                          </td>
                          <td data-label="Name">{guard.name}</td>
                          <td data-label="Email">{guard.email}</td>
                          <td data-label="Phone">{guard.phone}</td>
                          <td className="is-action" data-label="Action">
                            <div className="event-guard-actions">
                              <button
                                type="button"
                                className="event-guard-action-btn is-edit"
                                aria-label={`Edit ${guard.name}`}
                                onClick={() => {
                                  setSelectedGuard(guardId);
                                  setIsEditModal(true);
                                }}
                              >
                                <i
                                  className="bi bi-pencil-fill"
                                  aria-hidden="true"
                                ></i>
                              </button>
                              <button
                                type="button"
                                className="event-guard-action-btn is-delete"
                                aria-label={`Delete ${guard.name}`}
                                onClick={() => {
                                  setIsDeleteModal(true);
                                  setSelectedGuard(guardId);
                                }}
                              >
                                <i
                                  className="bi bi-trash-fill"
                                  aria-hidden="true"
                                ></i>
                              </button>
                              <button
                                type="button"
                                className="event-guard-action-btn is-key"
                                aria-label={`Change password for ${guard.name}`}
                                onClick={() => setChangePassword(guard)}
                              >
                                <i
                                  className="bi bi-key-fill"
                                  aria-hidden="true"
                                ></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="5" className="event-guard-empty">
                        No guards yet. Use Add Guard to create one.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </MainPagination>
          )}
        </div>
      </div>

      {isOpen && (
        <ModalWrapper
          title="Add Guard"
          submitText="Create"
          formId="assign-guard"
          CloseModalHandler={() => setIsOpen(false)}
          dialogClassName="event-guard-modal-dialog"
          contentClassName="event-guard-modal"
          headerClassName="event-guard-modal-header"
          bodyClassName="event-guard-modal-body"
          footerClassName="event-guard-modal-footer"
          closeClass="event-guard-modal-btn-close"
          submitClass="event-guard-modal-btn-primary"
        >
          <form
            className="event-guard-form"
            id="assign-guard"
            onSubmit={handleSubmit(addGuard)}
          >
            <div className="row">
              <Input
                ValidationArr={[{ Type: "required" }]}
                WrapperClass="col-lg-12"
                element="text"
                FieldName="name"
                Register={register}
                Errors={errors}
                placeholder={GuardLanguage.Assign.form.name.placeHolder}
                Label={GuardLanguage.Assign.form.name.label}
              />
              <Input
                ValidationArr={[{ Type: "required" }]}
                WrapperClass="col-lg-12"
                element="text"
                FieldName="email"
                Register={register}
                Errors={errors}
                placeholder={GuardLanguage.Assign.form.email.placeHolder}
                Label={GuardLanguage.Assign.form.email.label}
              />
              <Input
                ValidationArr={[{ Type: "required" }]}
                WrapperClass="col-lg-12"
                element="text"
                FieldName="userName"
                Register={register}
                Errors={errors}
                placeholder={GuardLanguage.Assign.form.username.placeHolder}
                Label={GuardLanguage.Assign.form.username.label}
              />
              <Input
                ValidationArr={[{ Type: "required" }]}
                WrapperClass="col-lg-12"
                element="password"
                FieldName="password"
                Register={register}
                Errors={errors}
                placeholder={GuardLanguage.Assign.form.password.placeHolder}
                Label={GuardLanguage.Assign.form.password.label}
              />
              <Input
                ValidationArr={[{ Type: "required" }]}
                WrapperClass="col-lg-12"
                element="date"
                FieldName="dob"
                Register={register}
                Errors={errors}
                Label={GuardLanguage.Assign.form.dob.label}
              />
              {dobError && (
                <div className="col mb-3">
                  <div className="text-danger" style={{ fontSize: "14px" }}>
                    {dobError}
                  </div>
                </div>
              )}
              <Input
                ValidationArr={[{ Type: "required" }]}
                WrapperClass="col-lg-12"
                element="select"
                FieldName="gender"
                Register={register}
                Errors={errors}
                Label={GuardLanguage.Assign.form.gender.label}
                options={gender}
                optionValue="title"
                optionLabel="title"
              />
              <Input
                ValidationArr={[{ Type: "required" }]}
                WrapperClass="col-lg-12"
                element="number"
                FieldName="phone"
                Register={register}
                Errors={errors}
                placeholder={GuardLanguage.Assign.form.phone.placeHolder}
                Label={GuardLanguage.Assign.form.phone.label}
              />
            </div>
          </form>
        </ModalWrapper>
      )}

      {isDeleteModal && (
        <ModalWrapper
          title={GuardLanguage.DeleteGuard.title}
          clickHandler={handleDeleteGuard}
          CloseModalHandler={() => setIsDeleteModal(false)}
          dialogClassName="event-guard-modal-dialog"
          contentClassName="event-guard-modal"
          headerClassName="event-guard-modal-header"
          bodyClassName="event-guard-modal-body"
          footerClassName="event-guard-modal-footer"
          closeClass="event-guard-modal-btn-close"
          submitClass="event-guard-modal-btn-primary"
        >
          <span>{GuardLanguage.DeleteGuard.message}</span>
        </ModalWrapper>
      )}

      {isEditModal && (
        <EditGuard
          setIsOpen={setIsEditModal}
          guardId={selectedGuard}
          getOrganizerGuardsList={getOrganizerGuardsList}
          setSelectedGuard={setSelectedGuard}
        />
      )}

      {!!changePassword && (
        <ChangePassword
          guard={changePassword}
          onClose={() => setChangePassword(null)}
        />
      )}
    </MainComponentWrapper>
  );
};

export default AssignGuard;
